import { observer } from 'mobx-react-lite';
import { Suspense, useContext, useEffect, useMemo } from 'react';
import { BrowserRouter, Redirect, Route, Switch, useHistory, useLocation } from 'react-router-dom';

import {
  CLIENT_FORM_ROUTES, EMPTY_STRING, PATIENT_ONBOARDING_ROUTES, PATIENT_ONBOARDING_ROUTING_CONFIG,
  paymentsTabRoute, ROUTING_CONFIG, STUDENT_ONBOARDING_ROUTES, STUDENT_ONBOARDING_ROUTING_CONFIG,
  BETTERHELP_ONBOARDING_ROUTES, BETTERHELP_ONBOARDING_ROUTING_CONFIG,
} from '@uplift/constants';
import {
  CurrentuserContext, sentryInit, TimezoneProvider, useCurrentuserRefetch
} from '@uplift/contexts';
import { useDispatchUserId, useStore } from '@uplift/hooks';
import { Page404 } from '@uplift/pages';
import { CurrentUserProvider } from '@uplift/providers';
import { LocalStorageService } from '@uplift/services';
import { SubSystem, UserStatus } from '@uplift/types';
import { isRestricted } from '@uplift/utils';
import { Spinner, AutoLogoutProvider, MessagingProvider, MainLayoutProvider, ProtectedRoute, UpliftThemeProvider, PatientMessagingModal } from '@uplift/ui-v2';

import { environment } from '../environments/environment';
import { VerifyEmailModal, VerifyPhone } from './components';
import {
  AppointmentSessionPage, DashboardPage, FormPage, NextAssessmentRedirect, ScheduleSessionPage, SettingsPage
} from './pages';
import { CareTeamProviderPage } from './pages/care-team/care-team-provider.page';
import { CareTeamPage } from './pages/care-team/care-team.page';
import { CheckForPsychMatch } from './pages/find-psychiatrist/check-for-match.page';
import { InsurancePage } from './pages/insurance';
import { MatchingPage } from './pages/matching';
import { OnboardingPage, StudentOnboardingPage, BetterhelpOnboardingPage } from './pages/onboarding';
import { PaymentsPage } from './pages/payments';
import { RematchingPage } from './pages/rematching/rematching.page';
import { BetterhelpSsoLandingPage } from './pages/betterhelp-sso-landing/betterhelp-sso-landing.page';
import { SupportPage } from './pages/support/support.page';
import { CampusPlan } from '@uplift/api';
import { useCurrentStudentDetails } from '@uplift/queries';

if (environment.production) {
  sentryInit(environment.sentryDSN);
}

const Router = observer(() => {
  const { authStore } = useStore();
  const currentUser = useContext(CurrentuserContext);
  const currentStudent = useCurrentStudentDetails();
  const { refetchUser } = useCurrentuserRefetch();
  const history = useHistory();
  const location = useLocation();
  const isRegular = currentUser?.status && currentUser?.status === UserStatus.regular;
  const isStudent = currentUser?.subsystem === SubSystem.higherEd;

  useEffect(() => {
    // if (
    //   !authStore.isLoggedIn
    //   && MATCHING_ROUTES.includes(location.pathname)
    // ) {
    //   authStore.setLoading(false);
    //   return;
    // }

    if (location.pathname === ROUTING_CONFIG.betterhelpSsoLanding) {
      authStore.setLoading(false);
      return;
    }

    authStore.tryRefresh(window.location, EMPTY_STRING, refetchUser);
  }, [ currentUser, authStore.isLoggedIn ]); // eslint-disable-line react-hooks/exhaustive-deps

  // If not initial -> push to matching
  useEffect(() => {
    if (isRegular === false) {
      // Check if patient subsystem
      switch (currentUser.subsystem) {
        case SubSystem.higherEd: {
          if (!location.pathname.includes(STUDENT_ONBOARDING_ROUTING_CONFIG.onboarding)) {
            history.push(STUDENT_ONBOARDING_ROUTING_CONFIG.onboarding);
          }
          return;
        }
        case SubSystem.betterhelp: {
          if (!location.pathname.includes(BETTERHELP_ONBOARDING_ROUTING_CONFIG.onboarding)) {
            history.push(BETTERHELP_ONBOARDING_ROUTING_CONFIG.onboarding);
          }
          return;
        }
        default: {
          if (!location.pathname.includes(PATIENT_ONBOARDING_ROUTING_CONFIG.onboarding)) {
            history.push(PATIENT_ONBOARDING_ROUTING_CONFIG.onboarding);
          }
          return;
        }
      }
    }
  }, [ isRegular, currentUser ]); // eslint-disable-line react-hooks/exhaustive-deps

  useDispatchUserId();

  const spinner = <Spinner />;

  const nonPaymentRestricted = useMemo(() => ({
    fn: isRestricted,
    fallback: () => null
  }), []);

  const isSponsoredStudent = [ CampusPlan.flex, CampusPlan.complete ].includes(currentStudent?.higherEdOrg?.contract?.plan?.type);

  if (authStore.loading) {
    return spinner;
  }

  return (
    <Suspense fallback={spinner}>
      <Switch>
        <Route exact path={ROUTING_CONFIG.betterhelpSsoLanding}>
          <BetterhelpSsoLandingPage />
        </Route>

        <Route
          exact
          path={[
            ROUTING_CONFIG.legacyOnboard,
            ROUTING_CONFIG.legacyOnboardConnect,
          ]}
        >
          <Redirect to={PATIENT_ONBOARDING_ROUTING_CONFIG.onboarding + history.location.search} />
        </Route>

        <Route
          exact
          path={ROUTING_CONFIG.invitation}
        >
          <Redirect
            to={PATIENT_ONBOARDING_ROUTING_CONFIG.onboarding + history.location.search.replace('invitation_token', 'invite_token')}
          />
        </Route>

        {!isRegular && !isStudent && (
          <Route
            exact
            path={PATIENT_ONBOARDING_ROUTES}
          >
            <OnboardingPage />
          </Route>
        )}

        {!isRegular && (
          <Route
            exact
            path={STUDENT_ONBOARDING_ROUTES}
          >
            <StudentOnboardingPage />
          </Route>
        )}

        {!isRegular && (
          <Route
            exact
            path={BETTERHELP_ONBOARDING_ROUTES}
          >
            <BetterhelpOnboardingPage />
          </Route>
        )}

        <ProtectedRoute
          exact
          path={ROUTING_CONFIG.matching}
          redirectUrl={environment.authUrl}
          blockIf={[ nonPaymentRestricted ]}
        >
          <MatchingPage />
        </ProtectedRoute>

        <ProtectedRoute
          exact
          path={ROUTING_CONFIG.formBySubmission}
          redirectUrl={environment.authUrl}
          blockIf={[ nonPaymentRestricted ]}
        >
          <FormPage />
        </ProtectedRoute>

        <ProtectedRoute
          exact
          path={CLIENT_FORM_ROUTES}
          redirectUrl={environment.authUrl}
          blockIf={[ nonPaymentRestricted ]}
        >
          <FormPage />
        </ProtectedRoute>

        <ProtectedRoute
          exact
          path={ROUTING_CONFIG.nextAssessment}
          redirectUrl={environment.authUrl}
          blockIf={[ nonPaymentRestricted ]}
        >
          <NextAssessmentRedirect />
        </ProtectedRoute>

        <Route exact path={ROUTING_CONFIG.support}>
          <SupportPage />
        </Route>

        <ProtectedRoute
          redirectUrl={environment.authUrl}
          exact
          path={ROUTING_CONFIG.main}
          blockIf={[ nonPaymentRestricted ]}
          allowIf={[ isRegular ]}
        >
          <DashboardPage />
        </ProtectedRoute>

        <ProtectedRoute
          redirectUrl={environment.authUrl}
          exact
          path={ROUTING_CONFIG.appointmentsSession}
          blockIf={[ nonPaymentRestricted ]}
        >
          <AppointmentSessionPage />
        </ProtectedRoute>

        <ProtectedRoute
          redirectUrl={environment.authUrl}
          exact
          path={ROUTING_CONFIG.appointmentsSession}
          allowIf={[ isRegular ]}
          blockIf={[ nonPaymentRestricted ]}
        >
          <AppointmentSessionPage />
        </ProtectedRoute>

        <ProtectedRoute
          redirectUrl={environment.authUrl}
          exact
          path={ROUTING_CONFIG.careTeam}
          blockIf={[ nonPaymentRestricted ]}
          allowIf={[ isRegular ]}
        >
          <CareTeamPage />
        </ProtectedRoute>

        <ProtectedRoute
          redirectUrl={environment.authUrl}
          exact={false}
          path={[ ROUTING_CONFIG.rematching, ROUTING_CONFIG.deeplinkRematching ]}
          allowIf={[ isRegular ]}
          blockIf={[ nonPaymentRestricted ]}
        >
          <RematchingPage />
        </ProtectedRoute>

        <ProtectedRoute
          redirectUrl={environment.authUrl}
          exact
          path={ROUTING_CONFIG.careTeamProfile}
          allowIf={[ isRegular ]}
          blockIf={[ nonPaymentRestricted ]}
        >
          <CareTeamProviderPage />
        </ProtectedRoute>

        <ProtectedRoute
          redirectUrl={environment.authUrl}
          fallbackUrl={ROUTING_CONFIG.payments}
          exact
          path={ROUTING_CONFIG.settings}
          blockIf={[ nonPaymentRestricted ]}
        >
          <SettingsPage />
        </ProtectedRoute>

        <ProtectedRoute
          redirectUrl={environment.authUrl}
          fallbackUrl={ROUTING_CONFIG.billing}
          exact
          path={ROUTING_CONFIG.billing}
        >
          <Redirect to={paymentsTabRoute.createLink({ tab: 'method' })} />
        </ProtectedRoute>

        <ProtectedRoute
          redirectUrl={environment.authUrl}
          fallbackUrl={ROUTING_CONFIG.payments}
          exact
          path={ROUTING_CONFIG.billing}
        >
          {/* this is fallback for back compatibility from /settings/billing to new payments */}
          <Redirect to={paymentsTabRoute.createLink({ tab: 'method' })} />
        </ProtectedRoute>

        <ProtectedRoute
          redirectUrl={environment.authUrl}
          fallbackUrl={ROUTING_CONFIG.dashboard}
          exact
          path={[ ROUTING_CONFIG.payments, ROUTING_CONFIG.paymentsTab ]}
          blockIf={[ isSponsoredStudent ]}
        >
          <PaymentsPage />
        </ProtectedRoute>

        <ProtectedRoute
          redirectUrl={environment.authUrl}
          fallbackUrl={ROUTING_CONFIG.payments}
          exact
          path={ROUTING_CONFIG.insurance}
        >
          <InsurancePage />
        </ProtectedRoute>

        <ProtectedRoute
          redirectUrl={environment.authUrl}
          exact
          path={ROUTING_CONFIG.findPsychiatrist}
          blockIf={[ nonPaymentRestricted ]}
        >
          <CheckForPsychMatch />
        </ProtectedRoute>
        <ProtectedRoute
          redirectUrl={environment.authUrl}
          exact
          path={ROUTING_CONFIG.clientScheduleSession}
          blockIf={[ nonPaymentRestricted ]}
        >
          <ScheduleSessionPage />
        </ProtectedRoute>

        <Route exact path={ROUTING_CONFIG.pageNotFound}>
          <Page404 />
        </Route>

        <Route path={ROUTING_CONFIG.any} render={() => <Redirect to={ROUTING_CONFIG.main} />} />
      </Switch>
      <VerifyEmailModal />
      <VerifyPhone />
    </Suspense>
  );
});

export const App = observer(() => {
  LocalStorageService.setAppNameSpace(environment.appNameSpace);
  const { authStore } = useStore();

  useEffect(() => {
    authStore.getTokens();
    if (authStore.needToFetchAccessToken) {
      authStore.fetchAccessTokenByRefreshToken();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (authStore.needToFetchAccessToken) {
    // TODO: loader
    return null;
  }

  return (
    <CurrentUserProvider isLoggedIn={authStore.isLoggedIn} accessToken={authStore?.tokens?.accessToken}>
      <TimezoneProvider>
        <UpliftThemeProvider>
          <AutoLogoutProvider>
            <BrowserRouter>
              <MainLayoutProvider>
                <MessagingProvider>
                  <Router />
                  <PatientMessagingModal />
                </MessagingProvider>
              </MainLayoutProvider>
            </BrowserRouter>
          </AutoLogoutProvider>
        </UpliftThemeProvider>
      </TimezoneProvider>
    </CurrentUserProvider>
  );
});
